import {
  onAuthStateChanged,
  onIdTokenChanged,
  signInAnonymously as fbSignInAnonymously,
  type User,
} from 'firebase/auth'
import { getFirebaseAuth } from './app'
import { shellStore, type AuthUser } from '@/store/shellStore'
import { AUTH_DEBOUNCE_MS } from '@/config/constants'
import { tr } from '@/i18n'

/* Port of auth_util.dart + firebase_user_provider.dart.
 *
 * Module-level mutable globals, exactly like the Dart file: `currentUserUid`
 * is read OUTSIDE React (the reassurance write's sendersUserId), so it lives
 * here, not in a store. Default '' — an anonymous user has null email/
 * displayName/photoURL, so those globals stay '' forever in this app. */

let currentUser: AuthUser | null = null

export function getCurrentUserUid(): string {
  // currentUserUid => currentUser?.uid ?? ''
  return currentUser?.uid ?? ''
}

export function isLoggedIn(): boolean {
  // loggedIn => currentUser?.loggedIn ?? false
  return currentUser?.loggedIn ?? false
}

function wrap(user: User | null): AuthUser {
  // H2HWebAppFirebaseUser: non-null wrapper, possibly-null user inside.
  return { uid: user?.uid ?? null, loggedIn: user != null }
}

let started = false
let pendingNull: ReturnType<typeof setTimeout> | null = null

/* Port of h2HWebAppFirebaseUserStream() + main.dart's `userStream..listen`.
 *
 * rxdart debounce semantics: every emission opens its own window — 1s for
 * `null` while not logged in, immediate otherwise — and a newer emission
 * cancels the pending one. This is what prevents the Welcome flash on
 * reload-with-session: the initial `null` is held for 1s, and the restored
 * user arrives first and cancels it. */
export function startUserStream(): void {
  if (started) return
  started = true

  const auth = getFirebaseAuth()

  onAuthStateChanged(auth, (raw) => {
    if (pendingNull) {
      clearTimeout(pendingNull)
      pendingNull = null
    }
    const emit = () => {
      // Post-debounce, exactly like the Dart .map: set the global, then
      // push into the shell store (main.dart: _appStateNotifier.update).
      currentUser = wrap(raw)
      shellStore.update(currentUser)
    }
    if (raw == null && !isLoggedIn()) {
      pendingNull = setTimeout(emit, AUTH_DEBOUNCE_MS)
    } else {
      emit()
    }
  })

  // jwtTokenStream.listen((_) {}) — subscribed purely to keep the token
  // fresh. The JS SDK refreshes tokens itself; the no-op listener is kept
  // for parity and as the future home of _currentJwtToken if ever needed.
  onIdTokenChanged(auth, () => {})
}

/* Port of FirebaseAuthManager.signInAnonymously via _signInOrCreateAccount:
 * on FirebaseAuthException -> snackbar `Error: ${message}` -> null.
 * (The email-already-in-use / INVALID_LOGIN_CREDENTIALS mappings belong to
 * the unreachable email flows and are not ported.)
 *
 * onError is the SnackBar seam — Tracking wires it to the toast. */
export async function signInAnonymouslyH2H(
  onError?: (message: string) => void,
): Promise<AuthUser | null> {
  try {
    const cred = await fbSignInAnonymously(getFirebaseAuth())
    return wrap(cred.user)
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    onError?.(tr.authError(message))
    return null
  }
}
